import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { apiGet, apiPost } from '../../utils/api';
import { Card, Spinner, Badge, Button } from '../design-system';
import { Users, Crown, User, Trash2, Shield, Mail, Calendar } from 'lucide-react';
import { toast } from 'react-hot-toast';

const PracticeMembersList = ({ onMemberUpdate }) => {
  const { userInfo } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [removing, setRemoving] = useState({});

  useEffect(() => {
    fetchMembers();
  }, []);

  const fetchMembers = async () => {
    try {
      setLoading(true);
      const response = await apiGet('/.netlify/functions/get-practice-members');
      if (response.members) {
        setMembers(response.members);
      }
    } catch (error) {
      console.error('Error fetching members:', error);
      toast.error('Failed to load practice members');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchMembers();
    if (onMemberUpdate) {
      onMemberUpdate();
    }
  };

  const handleRemoveMember = async (memberUserId, memberName) => {
    if (!window.confirm(`Are you sure you want to remove ${memberName} from the practice? This action cannot be undone.`)) {
      return;
    }

    try {
      setRemoving(prev => ({ ...prev, [memberUserId]: true }));
      await apiPost('/.netlify/functions/remove-member', {
        memberUserId: memberUserId
      });
      toast.success(`${memberName} has been removed from the practice`);
      fetchMembers();
      if (onMemberUpdate) {
        onMemberUpdate();
      }
    } catch (error) {
      console.error('Error removing member:', error);
      toast.error(error.message || 'Failed to remove member');
    } finally {
      setRemoving(prev => ({ ...prev, [memberUserId]: false }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Spinner size="large" />
      </div>
    );
  }

  if (members.length === 0) {
    return (
      <Card variant="default" padding="large">
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Users className="h-8 w-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Members Yet</h3>
          <p className="text-gray-600 mb-6">
            Start building your practice by inviting therapists to join.
          </p>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-600">
          {members.length} {members.length === 1 ? 'member' : 'members'}
        </p>
        <Button
          variant="ghost"
          size="small"
          onClick={handleRefresh}
        >
          Refresh
        </Button>
      </div>

      <div className="space-y-3">
        {members.map((member) => (
          <Card 
            key={member.practiceMemberId} 
            variant="default" 
            padding="medium"
            className="hover:shadow-md transition-shadow duration-200"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4 flex-1 min-w-0">
                <div className={`p-3 rounded-lg flex-shrink-0 ${
                  member.isAdmin ? 'bg-yellow-100' : 'bg-blue-100'
                }`}>
                  {member.isAdmin ? (
                    <Crown className="h-5 w-5 text-yellow-600" />
                  ) : (
                    <User className="h-5 w-5 text-blue-600" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="font-semibold text-gray-900 truncate">
                      {member.name}
                    </p>
                    {member.isCurrentUser && (
                      <Badge variant="outline" size="small">
                        You
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-sm text-gray-600 mb-2">
                    <div className="flex items-center gap-1">
                      <Mail className="h-3 w-3" />
                      <span className="truncate">{member.email}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {member.isAdmin && (
                      <Badge variant={member.isPrimaryAdmin ? "accent" : "success"} size="small">
                        <Shield className="h-3 w-3 mr-1" />
                        {member.isPrimaryAdmin ? 'Primary Admin' : 'Admin'}
                      </Badge>
                    )}
                    {!member.isAdmin && (
                      <Badge variant="default" size="small">
                        Therapist
                      </Badge>
                    )}
                    <div className="flex items-center gap-1 text-xs text-gray-500">
                      <Calendar className="h-3 w-3" />
                      <span>Joined {new Date(member.joinedAt).toLocaleDateString('en-US', { 
                        month: 'short', 
                        year: 'numeric' 
                      })}</span>
                    </div>
                  </div>
                </div>
              </div>
              {!member.isCurrentUser && userInfo?.practice?.isAdmin && (
                <div className="flex items-center gap-2 ml-4">
                  <Button
                    variant="ghost"
                    size="small"
                    onClick={() => handleRemoveMember(member.userId, member.name)}
                    disabled={removing[member.userId]}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    title="Remove member from practice"
                  >
                    {removing[member.userId] ? (
                      <Spinner size="small" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              )}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default PracticeMembersList;
